import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxInput,
  ComboboxInputGroup,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';

interface ModelEntry {
  id: string;
  displayName: string;
  deprecated?: boolean;
}

interface ModelTier {
  tier: string;
  efficiency: string;
  models: readonly ModelEntry[];
}

/** Case-insensitive contains, against both the friendly name and the id (FR-004). */
function matches(entries: readonly ModelEntry[], query: string): ModelEntry[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...entries];
  return entries.filter(
    (entry) =>
      entry.displayName.toLowerCase().includes(needle) ||
      entry.id.toLowerCase().includes(needle),
  );
}

/**
 * A searchable, grouped picker for the model an LLM Profile runs under
 * (spec 086), parameterized by catalog so both Claude and OpenAI (spec 160)
 * reuse the exact same search/grouping/free-text behavior — a drop-in
 * replacement for the plain text `Input` this field used before: any string
 * is still accepted (FR-006), this only adds a way to find and pick a known
 * one without typing it from memory.
 *
 * Built on the same combobox primitives as `AssigneePicker`/`LabelPicker`.
 * Unlike those, the input itself shows the current value's friendly name
 * (via `inputValue`), because this field's value is always meaningful and
 * required — there is no separate "nothing chosen" state to keep the input
 * free for searching, the way an optional assignee has.
 */
export function ModelCombobox({
  catalog: plainCatalog,
  idPrefix,
  value,
  onChange,
  id,
  placeholder,
  disabled = false,
  autoFocus,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: {
  catalog: readonly ModelTier[];
  /**
   * What the selected provider puts in front of every id in `catalog` (spec 205): the
   * catalog stays plain and the shown, searched and saved id is `idPrefix + id`.
   * Absent for catalogs whose ids are used as-is (Claude).
   */
  idPrefix?: string | null;
  value: string;
  onChange: (model: string) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}) {
  const catalog = useMemo(
    () =>
      idPrefix
        ? plainCatalog.map((tier) => ({
            ...tier,
            models: tier.models.map((model) => ({
              ...model,
              id: `${idPrefix}${model.id}`,
            })),
          }))
        : plainCatalog,
    [plainCatalog, idPrefix],
  );

  const allModels = useMemo(
    () => catalog.flatMap((tier) => tier.models),
    [catalog],
  );

  function findEntry(modelId: string): ModelEntry | undefined {
    return allModels.find((entry) => entry.id === modelId);
  }

  function findExactEntry(query: string): ModelEntry | undefined {
    const needle = query.trim().toLowerCase();
    if (!needle) return undefined;
    return allModels.find(
      (entry) =>
        entry.id.toLowerCase() === needle ||
        entry.displayName.toLowerCase() === needle,
    );
  }

  function isExactMatch(query: string): boolean {
    return findExactEntry(query) !== undefined;
  }

  const [query, setQuery] = useState(
    () => findEntry(value)?.displayName ?? value,
  );

  const filteredTiers = useMemo(
    () =>
      catalog
        .map((tier) => ({
          ...tier,
          models: matches(tier.models, query),
        }))
        .filter((tier) => tier.models.length > 0),
    [catalog, query],
  );

  const trimmedQuery = query.trim();
  const showFreeTextOption = trimmedQuery !== '' && !isExactMatch(query);

  function commit(model: string) {
    onChange(model);
    setQuery(findEntry(model)?.displayName ?? model);
  }

  return (
    <Combobox<string, false>
      value={value}
      onValueChange={(next) => {
        commit(next ?? '');
      }}
      inputValue={query}
      onInputValueChange={(next) => {
        setQuery(next);
        // Typing alone commits the typed text as the value, same as the plain
        // `Input` this replaces (FR-006, FR-010) — selecting a list item is a
        // convenience for finding a known id, not the only way to set one.
        // When the typed text exactly matches a known entry (including via the
        // primitive's own inline autocomplete fill), commit that entry's real
        // id rather than the friendly-name text sitting in the input.
        onChange(findExactEntry(next)?.id ?? next);
      }}
      items={[
        ...filteredTiers.flatMap((tier) => tier.models.map((m) => m.id)),
        ...(showFreeTextOption ? [query] : []),
      ]}
      itemToStringLabel={(modelId) =>
        findEntry(modelId)?.displayName ?? modelId
      }
      disabled={disabled}
    >
      <ComboboxInputGroup>
        <ComboboxInput
          id={id}
          placeholder={placeholder}
          className="font-mono"
          autoFocus={autoFocus}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
        />
      </ComboboxInputGroup>

      <ComboboxContent>
        <ComboboxList>
          {filteredTiers.map((tier) => (
            <ComboboxGroup key={tier.tier} items={tier.models.map((m) => m.id)}>
              <ComboboxGroupLabel>
                {tier.tier}
                <Badge variant="secondary">{tier.efficiency}</Badge>
              </ComboboxGroupLabel>
              {tier.models.map((entry) => (
                <ComboboxItem key={entry.id} value={entry.id}>
                  <span className="flex min-w-0 flex-col leading-tight">
                    <span className="truncate">
                      {entry.displayName}
                      {entry.deprecated ? (
                        <span className="text-muted-foreground ml-1.5 text-xs">
                          Deprecated
                        </span>
                      ) : null}
                    </span>
                    <span className="text-muted-foreground truncate font-mono text-xs">
                      {entry.id}
                    </span>
                  </span>
                </ComboboxItem>
              ))}
            </ComboboxGroup>
          ))}

          {showFreeTextOption ? (
            <ComboboxItem value={query}>
              <span className="truncate">Use &ldquo;{query}&rdquo;</span>
            </ComboboxItem>
          ) : null}
        </ComboboxList>

        {filteredTiers.length === 0 && !showFreeTextOption ? (
          <ComboboxEmpty>
            <span className="text-muted-foreground text-sm">
              No model matches &ldquo;{trimmedQuery}&rdquo;.
            </span>
          </ComboboxEmpty>
        ) : null}
      </ComboboxContent>
    </Combobox>
  );
}
